import React, { FormEvent, memo, useCallback } from 'react';
import { Button, Dropdown, Input, Modal } from '@cognitiv/cyprus-ui';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectManageParameter } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { clearParameter } from 'ducks/parameter/slices';
import { selectParameterMethodsList } from 'ducks/parameter-methods-list/selectors';
import { ParameterMethodsListState } from 'ducks/parameter-methods-list/slices';
import { selectProjectParameters } from 'ducks/project-parameters/selectors';
import { selectReferenceTypes } from 'ducks/reference-types-list/selectors';
import { selectSystemParametersList } from 'ducks/system-parameters-list/selectors';
import { SystemParametersListItem } from 'ducks/system-parameters-list/slices';
import { ERROR_LEVEL, ProjectParameter } from 'ducks/types/common-types';
import { ManageParameterProps } from 'ducks/types/prop-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { UnionFromArray } from 'ducks/types/type-helpers';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { ParameterMethodFields } from 'modals/parameters/components/ParameterMethodFields';
import { default_parameters } from 'modals/parameters/constants';
import { useManageParameterContext } from 'modals/parameters/context/ManageParameterContext';
import { shallowEqual } from 'react-redux';
import { getValidationError } from 'utils/errors';
import { getParameterSelectData, getSystemReferenceData } from 'utils/modal/helpers';

import cn from 'modals/parameters/components/Components.module.scss';

const ManageParameterModalBase = ({ onParameterChange, parameter_label = 'Value' }: ManageParameterProps) => {
  const dispatch = useAppDispatch();
  const { theme } = useSwitchTheme();

  const manage_parameter = useAppSelector(selectManageParameter, shallowEqual);
  const parameter_methods = useAppSelector(selectParameterMethodsList);
  const project_parameters = useAppSelector(selectProjectParameters);
  const reference_types = useAppSelector(selectReferenceTypes, shallowEqual);
  const system_parameters = useAppSelector(selectSystemParametersList);
  const { form, errors, validate, onChange, setForm } = useManageParameterContext();

  const onParameterMethodNameSelect = useCallback(
    ({ parameter_method_id, parameter_method_name }: UnionFromArray<ParameterMethodsListState>) => {
      const data = getParameterSelectData(parameter_method_id, parameter_method_name);
      onChange({
        command_line_prefix: data.command_line_prefix,
        command_line_assignment_char: data.command_line_assignment_char,
        command_line_escaped: data.command_line_escaped,
        command_line_ignore_name: data.command_line_ignore_name,
        is_encrypted: data.is_encrypted,
        parameter_method_id: data.parameter_method_id,
        parameter_method_name: data.parameter_method_name,
      });
    },
    [onChange],
  );

  const onReferenceTypeNameSelect = useCallback(
    (value: ProjectParameter) => {
      if (value) {
        onChange({
          parameter_value: '',
          reference_parameter_name: '',
          parameter_value_disabled: true,
          reference_type_name: value.reference_type_name,
          reference_type_id: value.reference_type_id,
        });
      } else {
        onChange({
          parameter_value_disabled: false,
          reference_type_name: '',
          reference_type_id: null,
          reference_id: null,
          reference_parameter_name: '',
        });
      }
    },
    [onChange],
  );

  const onReferenceParameterNameSelect = useCallback(
    (value: ProjectParameter) => {
      onChange({
        parameter_method_id: value.parameter_method_id,
        parameter_method_name: value.parameter_method_name,
        command_line_prefix: value.command_line_prefix,
        command_line_assignment_char: value.command_line_assignment_char,
        command_line_escaped: value.command_line_escaped,
        command_line_ignore_name: value.command_line_ignore_name,
        is_encrypted: value.is_encrypted,
        is_required: value.is_required,
        reference_parameter_name: value.parameter_name,
      });
    },
    [onChange],
  );

  const onSystemReferenceNameSelect = useCallback(
    (value: SystemParametersListItem) => {
      const data = getSystemReferenceData(value);
      onChange({ reference_parameter_name: data.reference_parameter_name });
    },
    [onChange],
  );
  const onParameterValueChange = useCallback((item: string) => onChange({ parameter_value: item }), [onChange]);
  const onDescriptionChange = useCallback((item: string) => onChange({ description: item }), [onChange]);
  const onParameterNameChange = useCallback((item: string) => onChange({ parameter_name: item }), [onChange]);

  const {
    parameter_name,
    parameter_value_disabled,
    reference_type_name,
    reference_parameter_name,
    parameter_value,
    parameter_method_name,
    description,
    parameter_direction_id,
    reference_type_id,
    parameter_method_id,
  } = form;

  const handleModalClose = () => {
    dispatch(setModal({ manage_parameter: false }));
    dispatch(clearParameter());
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const is_valid = validate();

    if (!is_valid) return;

    onParameterChange(form);
    setForm({ ...form, ...default_parameters });
    dispatch(setModal({ manage_parameter: false }));
    dispatch(clearParameter());
  };

  return (
    <Modal
      name="manage_parameter"
      show={manage_parameter}
      width={500}
      padding={0}
      close_box={50}
      onClose={handleModalClose}
      standard={false}
      theme={{
        modal_background: theme.element_background,
        modal_close_color: theme.font_color,
      }}
    >
      <h2
        className={cn.header}
        style={{
          backgroundColor: theme.modal.modal_background,
          color: theme.font_color,
          borderBottom: theme.border,
        }}
      >
        Manage Parameter
      </h2>
      <form className={cn.body}>
        <Input
          auto_focus
          label="Parameter Name"
          value={parameter_name ?? ''}
          onChange={onParameterNameChange}
          error_message={errors.find((error) => error.key === 'parameter_name')?.error}
          error_level={
            getValidationError<UnionFromArray<ProjectParameters>>({ errors, key: 'parameter_name' })
              ? ERROR_LEVEL.ERROR
              : undefined
          }
        />
        <Input
          label={`Parameter ${parameter_label}`}
          margin="16px 0px 0px 0px"
          value={
            (parameter_value_disabled ? `${reference_type_name}: ${reference_parameter_name}` : parameter_value) ?? ''
          }
          disabled={parameter_value_disabled}
          onChange={onParameterValueChange}
        />
        <Input label="Description" margin="16px 0px 0px 0px" value={description ?? ''} onChange={onDescriptionChange} />
        {parameter_direction_id === 1 && (
          <>
            <Dropdown
              label="Reference Entity"
              options={reference_types}
              option_key="reference_type_name"
              value={reference_type_name}
              margin="16px 0px 0px 0px"
              onSelect={onReferenceTypeNameSelect}
            />
            {reference_type_id === 3 && (
              <Dropdown
                label="Reference Parameter"
                options={project_parameters}
                option_key="parameter_name"
                value={reference_parameter_name}
                margin="16px 0px 0px 0px"
                onSelect={onReferenceParameterNameSelect}
              />
            )}
            {reference_type_id === 6 && (
              <Dropdown
                label="Reference Parameter"
                options={system_parameters}
                option_key="system_parameter_name"
                value={reference_parameter_name}
                margin="16px 0px 0px 0px"
                onSelect={onSystemReferenceNameSelect}
              />
            )}
            <Dropdown
              label="Method"
              options={parameter_methods}
              option_key="parameter_method_name"
              value={parameter_method_name}
              margin="16px 0px 0px 0px"
              onSelect={onParameterMethodNameSelect}
            />
            <ParameterMethodFields id={parameter_method_id} />
          </>
        )}
        <div className={cn.buttonContainer}>
          <Button onClick={onSubmit} width={115} variant="blue" button_size="medium" margin="16px 0px 0px 0px">
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export const ManageParameterModal = memo(ManageParameterModalBase);
